import { fetchEventSource } from '@microsoft/fetch-event-source'
import { ApiError, authHeaders, handleUnauthorized, readErrorDetail } from './client'
import type { ChatEvent, ChatEventName, ChatRequest } from './types'

const KNOWN_EVENTS: ReadonlySet<string> = new Set<ChatEventName>([
  'conversation',
  'user_message',
  'status',
  'activity',
  'sources',
  'progress',
  'delta',
  'done',
  'verification',
  'error',
])

/** Thrown to stop fetch-event-source from retrying; never surfaces to callers. */
class FatalStreamError extends Error {
  constructor(cause: unknown) {
    super('stream failed', { cause })
  }
}

/**
 * POST /api/chat and dispatch each SSE frame. Resolves when the server closes
 * the stream or when `signal` is aborted (the caller decides what an abort
 * means). Rejects with ApiError for HTTP/network failures.
 */
export async function streamChat(
  request: ChatRequest,
  onEvent: (event: ChatEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(await authHeaders()),
  }

  try {
    await fetchEventSource('/api/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
      // Never let the library re-POST when the tab is hidden/visible again.
      openWhenHidden: true,
      async onopen(response) {
        const type = response.headers.get('content-type') ?? ''
        if (response.ok && type.includes('text/event-stream')) return
        const detail = response.ok ? 'Unexpected response from the server.' : await readErrorDetail(response)
        if (response.status === 401) handleUnauthorized()
        throw new FatalStreamError(new ApiError(response.status, detail))
      },
      onmessage(message) {
        if (!message.event || !KNOWN_EVENTS.has(message.event)) return
        let data: unknown
        try {
          data = message.data ? JSON.parse(message.data) : {}
        } catch {
          return
        }
        onEvent({ event: message.event, data } as ChatEvent)
      },
      onerror(error) {
        // Rethrowing disables the library's automatic retry.
        throw error instanceof FatalStreamError ? error : new FatalStreamError(error)
      },
    })
  } catch (error) {
    if (signal.aborted) return
    const cause = error instanceof FatalStreamError ? error.cause : error
    if (cause instanceof ApiError) throw cause
    throw new ApiError(0, 'The connection was interrupted. Please try again.')
  }
}
